import { Injectable } from '@angular/core';
import {
  CancelReservationDialogComponent,
  CancelReservationDialogOutput
} from '../../components/dialogs/cancel-reservation-dialog/cancel-reservation-dialog.component';
import {
  CancellationReason,
  CustomerServiceNote,
  Itinerary,
  ItineraryService,
  LocationService,
  MapsService,
  Reservation,
  ReservationService,
  RideRequestLocation
} from '@uzurv/clara';
import { ReservationEventService, ReservationListItemVm } from '../../components/reservation';
import { catchError, filter, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ApplicationException, ObjectConstants, PermissionsConstants, isNullOrUndefined } from '@uzurv/core';
import { LocationManagerDialogComponent } from '../../components/dialogs/location-manager-dialog/location-manager-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthDataService } from '@uzurv/http';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import { CustomerServiceNoteService } from '@uzurv/clara';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class RideCancellationService {
  constructor(
    private reservationService: ReservationService,
    private itineraryService: ItineraryService,
    private mapService: MapsService,
    private locationService: LocationService,
    private dialog: MatDialog,
    private reservationEventService: ReservationEventService,
    private authDataService: AuthDataService,
    private customerServiceNoteService: CustomerServiceNoteService
  ) {}

  cancelRide(trip: ReservationListItemVm): Observable<Itinerary | null> {
    return this.openCancelReservationDialog(trip).pipe(
      switchMap(cancellationData => {
        if (!cancellationData.isRecovery) {
          // Cancel Reservation
          return this.handleRideCancellation(cancellationData).pipe(
            switchMap(() => {
              if (cancellationData.note !== '') {
                return this.createCancellationReasonNote(cancellationData).pipe(
                  map(() => {
                    this.openCancelRideConfirmationDialog(trip);
                    return null;
                  })
                )
              } else {
                this.openCancelRideConfirmationDialog(trip);
                return of(null);
              }
            })
          );
        } else {
          // Cancel Reservation and create Recovery Ride
          return this.handleRideCancellationWithRecovery(cancellationData).pipe(
            switchMap(result =>
              this.openCancelRecoveryRideConfirmationDialog(result).pipe(
                map(confirmed => {
                  // Confirms Recovery Ride to open in new tab
                  // Only time cancelRide will return Itinerary
                  if (confirmed) {
                    return result;
                  }
                  return null;
                })
              )
            )
          );
        }
      }),
      catchError((error: ApplicationException) => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Error',
            message: error.message
          }
        });
        return throwError(() => error);
      })
    );
  }

  private openCancelReservationDialog(trip: ReservationListItemVm): Observable<CancelReservationDialogOutput> {
    if (isNullOrUndefined(trip) || isNullOrUndefined(trip.rideRequestDetail)) {
      return throwError(() => new Error('Trip or Ride details are missing.'));
    }

    return this.dialog
      .open(CancelReservationDialogComponent, {
        data: {
          ride: trip
        }
      })
      .afterClosed()
      .pipe(
        filter(cancelReservationDialogOutput => !isNullOrUndefined(cancelReservationDialogOutput)),
        switchMap((cancelReservationDialogOutput: CancelReservationDialogOutput) => of(cancelReservationDialogOutput))
      );
  }

  private openCancelRecoveryRideConfirmationDialog(recoveryItinerary: Itinerary): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Success',
          message: `
                      Reservation has been cancelled.
                      <br />Recovery ride Req: ${recoveryItinerary.rideRequests[0].id} has been created.
                      <br />Click continue to open the ride details`,
          isDestructive: false,
          confirmText: 'Continue',
          dismissText: 'Cancel'
        }
      })
      .afterClosed();
  }

  private openCancelRideConfirmationDialog(trip: ReservationListItemVm): void {
    this.dialog.open(AlertDialogComponent, {
      data: {
        title: 'Success',
        message: trip.isReservation
          ? `Reservation with ID ${trip.id} was successfully cancelled`
          : `Ride request with ID ${trip.rideRequestDetail.id} was successfully cancelled`
      }
    });
  }

  private handleRideCancellation(rideCancellationData: CancelReservationDialogOutput): Observable<Reservation | boolean> {
    if (!isNullOrUndefined(rideCancellationData.ride.driver)) {
      // Cancel Reservation
      return this.cancelReservation(rideCancellationData.ride.id, rideCancellationData.cancellationReason);
    } else {
      // Cancel RideRequest
      return this.cancelRideRequest(
        rideCancellationData.ride.rideRequestDetail.itineraryId,
        rideCancellationData.ride.rideRequestDetail.id,
        rideCancellationData.cancellationReason.id
      );
    }
  }

  private handleRideCancellationWithRecovery(rideCancellationData: CancelReservationDialogOutput): Observable<Itinerary> {
    // Cancel Reservation and create Recovery Ride with Incident
    if (rideCancellationData.isIncident) {
      return this.getReservationRecoveryLocation(rideCancellationData).pipe(
        switchMap((rideRequestLocation: RideRequestLocation) => {
          return this.reservationService.cancelReservationAndCreateRecoveryRide(
            rideCancellationData.ride.id,
            rideCancellationData.cancellationReason,
            rideRequestLocation,
            rideCancellationData.note
          );
        })
      );
    } else {
      // Cancel Reservation and create Recovery Ride without Incident
      return this.reservationService.cancelReservationAndCreateRecoveryRide(
        rideCancellationData.ride.id,
        rideCancellationData.cancellationReason,
        rideCancellationData.ride.rideRequestDetail.pickupLocation,
        rideCancellationData.note
      );
    }
  }

  private createCancellationReasonNote(cancelReservationDialogOutput: CancelReservationDialogOutput): Observable<CustomerServiceNote> {
    const customerServiceNote = new CustomerServiceNote();

    if (isNullOrUndefined(cancelReservationDialogOutput.ride.driver)) {
      customerServiceNote.objectId = cancelReservationDialogOutput.ride.rideRequestDetail.id;
      customerServiceNote.objectName = ObjectConstants.RIDE_REQUEST;
    } else {
      customerServiceNote.objectId = cancelReservationDialogOutput.ride.id;
      customerServiceNote.objectName = ObjectConstants.RESERVATION;
    }

    customerServiceNote.text = cancelReservationDialogOutput.note;

    return this.customerServiceNoteService.createCustomerServiceNote(customerServiceNote);
  }

  private hasWebSelfServiceCancelPermission(): boolean {
    return this.authDataService.hasPermission({ any: [], all: [PermissionsConstants.WEB_SELF_SERVICE_RIDE_CANCEL] });
  }

  private cancelReservation(reservationId: number, cancellationReason: CancellationReason): Observable<Reservation> {
    if (this.hasWebSelfServiceCancelPermission()) {
      return this.reservationService.cancelReservationWithCancellationReasonWebSelfServiceCancel(reservationId, cancellationReason).pipe(
        map((reservation: Reservation) => {
          this.reservationEventService.reservationCancelled();
          return reservation;
        })
      );
    } else {
      return this.reservationService.cancelReservationWithCancellationReason(reservationId, cancellationReason).pipe(
        map((reservation: Reservation) => {
          this.reservationEventService.reservationCancelled();
          return reservation;
        })
      );
    }
  }

  private cancelRideRequest(itineraryId: number, rideRequestDetailId: number, cancellationReasonId: number): Observable<boolean> {
    if (this.hasWebSelfServiceCancelPermission()) {
      return this.itineraryService.webSelfServiceCancelRideRequest(itineraryId, rideRequestDetailId, cancellationReasonId).pipe(
        map((response: boolean) => {
          this.reservationEventService.rideRequestCancelled();
          return response;
        })
      );
    } else {
      return this.itineraryService.cancelRideRequest(itineraryId, rideRequestDetailId, cancellationReasonId).pipe(
        map((response: boolean) => {
          this.reservationEventService.rideRequestCancelled();
          return response;
        })
      );
    }
  }

  private getReservationRecoveryLocation(cancellationData: CancelReservationDialogOutput): Observable<RideRequestLocation> {
    return this.reservationService.getDriverPosition(cancellationData.ride.id).pipe(
      switchMap(miniDriver =>
        this.mapService.geocodeLocationByLatLng(miniDriver.latitude, miniDriver.longitude).pipe(
          switchMap(geocodeResult =>
            this.locationService.addLocation(geocodeResult.display).pipe(
              switchMap(location =>
                this.dialog
                  .open(LocationManagerDialogComponent, {
                    height: '90vh',
                    width: '90vw',
                    panelClass: 'location-manager-dialog-panel',
                    data: {
                      title: 'Confirm Recovery Ride Pick-up Location',
                      preloadedLocation: location
                    }
                  })
                  .afterClosed()
                  .pipe(
                    switchMap((rideRequestLocation: RideRequestLocation) => {
                      if (isNullOrUndefined(rideRequestLocation)) {
                        return throwError(() => new Error('Unable to retrieve Recovery Ride Pick-up Location '));
                      }
                      return of(rideRequestLocation);
                    })
                  )
              )
            )
          )
        )
      )
    );
  }
}
